import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import cx from 'classnames';
import { IMedispanApprovedDiff } from 'models/application-manager/medispan/IMedispanApprovedDiff';
import { medispanStagingStyles } from './index.styles';

interface IMedispanReviewedTableProps extends WithStyles<typeof medispanStagingStyles> {
  rows: IMedispanApprovedDiff[];
}

const formatAuditLabel = (row: IMedispanApprovedDiff): string => {
  const name = row.approver_display_name || `User #${row.approved_by}`;
  const date = new Date(row.approved_at);
  const time = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const datePart = date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  return `Approved by ${name} - ${time} ${datePart}`;
};

const MedispanReviewedTable = (props: IMedispanReviewedTableProps): JSX.Element => {
  const { classes, rows } = props;

  if (rows.length === 0) {
    return <Typography className={classes.emptyState}>No reviewed items found.</Typography>;
  }

  return (
    <TableContainer component={Paper} elevation={0} className={classes.tableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHeadCell} rowSpan={2}>
              Drug Descriptor ID
            </TableCell>
            <TableCell className={classes.tableHeadCell} colSpan={2}>
              GPI
            </TableCell>
            <TableCell className={classes.tableHeadCell} colSpan={2}>
              GPI 10
            </TableCell>
            <TableCell className={classes.tableHeadCell} colSpan={2}>
              Drug Name
            </TableCell>
            <TableCell className={classes.tableHeadCell} rowSpan={2}>
              User
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.subHeaderCell}>Current</TableCell>
            <TableCell className={classes.subHeaderCellRight}>Staged</TableCell>
            <TableCell className={classes.subHeaderCell}>Current</TableCell>
            <TableCell className={classes.subHeaderCellRight}>Staged</TableCell>
            <TableCell className={classes.subHeaderCell}>Current</TableCell>
            <TableCell className={classes.subHeaderCell}>Staged</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => {
            const renderPair = (
              currentVal: string | null | undefined,
              stagedVal: string | null | undefined,
            ) => {
              const cur = currentVal ?? '—';
              const stg = stagedVal ?? '—';
              const isDifferent = cur !== stg;
              return (
                <>
                  <TableCell className={classes.tableCell}>{cur}</TableCell>
                  <TableCell
                    className={cx(classes.tableCell, { [classes.changedValue]: isDifferent })}
                  >
                    {stg}
                  </TableCell>
                </>
              );
            };

            return (
              <TableRow
                key={row.id}
                className={cx(
                  classes.parentRow,
                  row.id % 2 === 0 ? classes.evenParent : classes.oddParent,
                )}
              >
                <TableCell className={classes.tableCell}>{row.drug_descriptor_id}</TableCell>
                {renderPair(row.gpi_current, row.gpi_staged)}
                {renderPair(row.gpi10_current, row.gpi10_staged)}
                {renderPair(row.drug_name_current, row.drug_name_staged)}
                <TableCell className={classes.userCell}>{formatAuditLabel(row)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const MedispanReviewedTableStyled = withStyles(medispanStagingStyles)(MedispanReviewedTable);
